import React, { memo, useMemo } from 'react';

interface Props {
	/** Base viewer URL of the DOP-converted presentation (its `contentUrl`). */
	contentUrl: string;
	/** 1-indexed current slide, from `PresentedMediaItem.currentPage`. */
	currentPage: number;
	/** Called once the iframe has loaded, to match the img/video loaded-count flow. */
	onLoad?: () => void;
	/**
	 * When a canvas drag is in progress, the iframe must stop capturing pointer
	 * events so native `dragover` reaches the parent `.media` drop area (iframes
	 * are separate browsing contexts and otherwise swallow the drag).
	 */
	globalIsDragging?: boolean;
}

/**
 * Renders a DigitalOfficePro (DOP) presentation as an iframe inside a canvas
 * feed slot. Slide navigation is driven purely by the URL fragment
 * (`#slideNumber=N`), which the browser resolves without reloading the document.
 */
export default memo(({ contentUrl, currentPage, onLoad, globalIsDragging }: Props) => {
	// The stored contentUrl already carries a `#slideNumber=1` fragment; strip any
	// existing hash before applying the current slide so we don't stack fragments.
	const src = useMemo(() => {
		const base = contentUrl.split('#')[0];
		return `${base}#slideNumber=${currentPage}`;
	}, [contentUrl, currentPage]);

	return (
		<iframe
			className="media-iframe"
			src={src}
			title="presentation"
			onLoad={onLoad}
			style={{ pointerEvents: globalIsDragging ? 'none' : undefined }}
			sandbox="allow-scripts allow-same-origin"
			referrerPolicy="strict-origin-when-cross-origin"
		/>
	);
});
